import { useMemo, useState } from 'react'
import { X, Check } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  useUserPreferences,
  useVendorsByIds,
  useTrackVendor,
  useUntrackVendor,
  VENDOR_SEARCH_MAX_LENGTH,
  VENDOR_SEARCH_PAGE,
} from '@/hooks/queries'
import { useVendorPickerSearch } from '@/hooks/use-vendor-picker-search'
import { useAuth } from '@/contexts/AuthContext'
import { trackVendorTracked } from '@/lib/analytics-events'

/**
 * Settings — Tracked vendors.
 *
 * Wave-1 P5 settings primitive UI. Lets the user track / untrack vendors;
 * tracked vendors boost ranking via `usePersonalisedScore`.
 *
 * KAN-822 P2: the directory is bigger than one page (the pipeline creates
 * vendors from extraction), so the section no longer filters a loaded first
 * page. Tracked vendors are resolved BY ID (`GET /api/vendors?ids=`) and are
 * always listed first; vendors to track come from a server-side search
 * (`GET /api/vendors?q=`, one page; an empty search browses the first page).
 * Click-to-toggle, no combobox dependency. Search results are offered only
 * for the filter as typed (never an earlier query's page), and a failed search
 * says so with a retry instead of looking like no matches. Search feedback sits
 * in regions that stay mounted (a polite status, and an alert for a failure)
 * so screen readers announce it. A failed tracked-vendor lookup is an alert
 * with a retry, never an empty or partial list presented as complete.
 *
 * A tracked id the lookup no longer resolves (the vendor left the directory)
 * has no button to untrack it, so the section counts those ids and offers to
 * remove them through the same untrack mutation. The count is shown only once
 * the lookup has succeeded, never while it loads or after it failed.
 */
export function TrackedVendorsSection() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences } = useUserPreferences(userId)
  const trackedIds = preferences?.trackedVendors ?? []
  const trackedLookup = useVendorsByIds(trackedIds)
  const trackVendor = useTrackVendor()
  const untrackVendor = useUntrackVendor()

  const [filter, setFilter] = useState('')
  // The removal status belongs to the orphan set it was about: 'done' to the empty set it left
  // behind, 'pending'/'failed' to the set it acted on. A different set shows no stale status.
  const [orphanRemovalState, setOrphanRemovalState] = useState<{
    status: 'idle' | 'pending' | 'done' | 'failed'
    key: string
    shown: boolean
  }>({ status: 'idle', key: '', shown: false })
  const search = useVendorPickerSearch(filter)
  const tracked = useMemo(() => new Set(trackedIds), [trackedIds])

  const trackedVendors = useMemo(
    () => [...(trackedLookup.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [trackedLookup.data],
  )
  const orphanIds = useMemo(() => {
    if (trackedLookup.isSuccess !== true) return []
    const resolved = new Set((trackedLookup.data ?? []).map((v) => v.id.toLowerCase()))
    return trackedIds.filter((id) => !resolved.has(id.toLowerCase()))
  }, [trackedLookup.isSuccess, trackedLookup.data, trackedIds])
  const orphanKey = orphanIds.join(',')
  // Once a status has been shown for its set and the set then changes, it is cleared for good,
  // so it cannot reappear if the set later returns to the same key (codex 7c1434ca).
  if (orphanRemovalState.status !== 'idle' && orphanRemovalState.status !== 'pending') {
    const matches = orphanRemovalState.key === orphanKey
    if (matches && !orphanRemovalState.shown) setOrphanRemovalState({ ...orphanRemovalState, shown: true })
    else if (!matches && orphanRemovalState.shown) setOrphanRemovalState({ status: 'idle', key: '', shown: false })
  }
  const orphanRemoval = orphanRemovalState.key === orphanKey ? orphanRemovalState.status : 'idle'
  const results = search.results
  const vendors = useMemo(() => {
    const shown = new Set(trackedVendors.map((v) => v.id))
    return [...trackedVendors, ...results.filter((v) => !shown.has(v.id))]
  }, [trackedVendors, results])
  const searching = filter.trim() !== ''
  const lookupFailed = trackedLookup.isError === true
  const lookupLoading = trackedIds.length > 0 && trackedLookup.isPending === true

  function toggle(vendorId: string) {
    if (tracked.has(vendorId)) {
      untrackVendor.mutate({ userId, vendorId })
    } else {
      trackVendor.mutate({ userId, vendorId })
      // KAN-32 vendor:tracked — track branch only; untracking emits nothing.
      trackVendorTracked(vendors, vendorId)
    }
  }

  async function removeOrphans() {
    setOrphanRemovalState({ status: 'pending', key: orphanKey, shown: false })
    const outcomes = await Promise.allSettled(
      orphanIds.map((vendorId) => untrackVendor.mutateAsync({ userId, vendorId })),
    )
    // A partial failure is keyed to the ids that are still tracked afterwards (the failed ones),
    // so the alert stays once the successful deletes refresh the list (Cubic 41468851).
    const failedKey = orphanIds.filter((_, i) => outcomes[i]?.status === 'rejected').join(',')
    setOrphanRemovalState(
      failedKey === '' ? { status: 'done', key: '', shown: false } : { status: 'failed', key: failedKey, shown: false },
    )
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium">Tracked vendors</p>
        <p className="text-xs text-muted-foreground">
          Boost ranking of advisories and stories that involve these vendors.
        </p>
      </div>

      <Input
        aria-label="Filter vendors"
        placeholder="Search vendors…"
        maxLength={VENDOR_SEARCH_MAX_LENGTH}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {/* Kept mounted so screen readers announce each change (codex on b8b1226f). */}
      <div role="status" aria-live="polite" aria-label="Vendor search">
        {lookupLoading && (
          <p className="text-xs italic text-muted-foreground">Loading your tracked vendors…</p>
        )}
        {search.searching && (
          <p className="text-xs italic text-muted-foreground">Searching…</p>
        )}
        {searching && search.ready && results.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            No vendors match &ldquo;{filter}&rdquo;.
          </p>
        )}
        {search.ready && results.length >= VENDOR_SEARCH_PAGE && (
          <p className="text-xs text-muted-foreground">
            Showing the first {VENDOR_SEARCH_PAGE} matches. Narrow your search to see more.
          </p>
        )}
        {orphanIds.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {orphanIds.length === 1
              ? '1 tracked vendor is no longer listed.'
              : `${orphanIds.length} tracked vendors are no longer listed.`}
          </p>
        )}
        {orphanRemoval === 'done' && <p className="text-xs text-muted-foreground">Removed.</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div role="alert">
          {lookupFailed && (
            <p className="text-xs text-muted-foreground">Your tracked vendors could not be loaded.</p>
          )}
          {search.failed && (
            <p className="text-xs text-muted-foreground">Vendor search failed.</p>
          )}
          {orphanRemoval === 'failed' && (
            <p className="text-xs text-muted-foreground">They could not be removed.</p>
          )}
        </div>
        {orphanIds.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label="Remove vendors that are no longer listed"
            disabled={!userId || orphanRemoval === 'pending'}
            onClick={() => void removeOrphans()}
          >
            Remove
          </Button>
        )}
        {lookupFailed && (
          <Button type="button" size="sm" variant="outline" onClick={() => void trackedLookup.refetch()}>
            Reload tracked vendors
          </Button>
        )}
        {search.failed && (
          <Button type="button" size="sm" variant="outline" onClick={search.retry}>
            Try again
          </Button>
        )}
      </div>
      {vendors.length === 0 ? (
        !searching && search.ready && !lookupFailed && !lookupLoading ? (
          <p className="text-xs text-muted-foreground italic">
            No vendors available.
          </p>
        ) : null
      ) : (
        <div
          className="flex max-h-72 flex-wrap gap-2 overflow-y-auto rounded-md border p-2"
          role="group"
          aria-label="Tracked vendors"
        >
          {vendors.map((v) => {
            const isTracked = tracked.has(v.id)
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => toggle(v.id)}
                aria-pressed={isTracked}
                disabled={!userId}
                className={
                  'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition ' +
                  (isTracked
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'hover:bg-muted')
                }
              >
                {isTracked ? (
                  <Check className="h-3 w-3" aria-hidden />
                ) : null}
                {v.name}
                {isTracked ? (
                  <X
                    className="h-3 w-3 text-muted-foreground"
                    // R28: the vendor the user TRACKS, in an attribute.
                    data-ph-no-capture
                    aria-label={`Untrack ${v.name}`}
                  />
                ) : null}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
